import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Sparkles, Calendar, Download } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

export function LearningCenterListPage() {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat) params.category = cat;
      const r = await api.get("/articles/list", { params });
      setItems(r.data.articles || []);
      setCategories(r.data.categories || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cat]); // eslint-disable-line

  const featured = items.find((a) => a.featured);
  const rest = items.filter((a) => a !== featured);

  return (
    <div className="min-h-screen bg-white text-charcoal">
      <SiteHeader />
      <main>
        <section className="bg-charcoal text-linen">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <p className="text-[11px] uppercase tracking-[0.32em] font-semibold text-gold-500">{L("Trung Tâm Học Tập", "Learning Center")}</p>
            <h1 className="font-serif text-4xl md:text-6xl mt-3 leading-tight max-w-4xl">{L("Kiến thức kinh doanh cho ngành làm đẹp Việt", "Business education for the Vietnamese beauty industry")}</h1>
            <p className="mt-5 text-lg text-warmgray-200 max-w-3xl">{L("Bài viết, hướng dẫn, và best practices về thuế, bảng lương, vận hành, marketing, và công nghệ — tất cả đều có thể được AsterPOS AI dùng để trả lời câu hỏi của bạn.", "Articles, guides, and best practices on taxes, payroll, operations, marketing, and technology — all of which AsterPOS AI uses to answer your questions.")}</p>
          </div>
        </section>

        <section className="border-b border-warmgray-200 sticky top-0 z-10 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 overflow-x-auto">
            <button type="button" onClick={() => setCat("")} data-testid="lc-cat-all"
              className={`text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full whitespace-nowrap ${!cat ? "bg-charcoal text-white" : "text-warmgray-700 hover:bg-warmgray-100"}`}>{L("Tất cả", "All")}</button>
            {categories.map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)} data-testid={`lc-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full whitespace-nowrap ${cat === c ? "bg-charcoal text-white" : "text-warmgray-700 hover:bg-warmgray-100"}`}>{c}</button>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 md:px-10 py-12">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-warmgray-700">{L("Sắp có bài viết. Quay lại sau nhé!", "Articles coming soon. Check back later!")}</div>
          ) : (
            <>
              {featured && (
                <Link to={`/learn/${featured.slug}`} data-testid={`lc-featured-${featured.id}`}
                  className="block bg-linen rounded-3xl overflow-hidden border border-warmgray-200 hover:border-emerald2 transition-all mb-10 group">
                  <div className="grid md:grid-cols-2">
                    <div className="aspect-[16/10] md:aspect-auto bg-warmgray-100 overflow-hidden">
                      {featured.featured_image_url ? <img src={featured.featured_image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" /> : <div className="w-full h-full flex items-center justify-center font-serif text-6xl text-warmgray-500">{featured.title?.[0]}</div>}
                    </div>
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-gold-500"><Sparkles className="w-3.5 h-3.5" /> {L("Nổi bật", "Featured")}</div>
                      <h2 className="font-serif text-3xl md:text-5xl mt-3 text-charcoal leading-tight">{featured.title}</h2>
                      {featured.summary && <p className="mt-4 text-warmgray-700 line-clamp-3">{featured.summary}</p>}
                      <div className="mt-6 flex items-center gap-3 text-xs text-warmgray-500">
                        <span>{featured.category}</span>{featured.author && <><span>·</span><span>{featured.author}</span></>}{featured.publish_date && <><span>·</span><span>{featured.publish_date}</span></>}
                      </div>
                    </div>
                  </div>
                </Link>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((a) => (
                  <Link key={a.id} to={`/learn/${a.slug}`} data-testid={`lc-card-${a.id}`}
                    className="block bg-white border border-warmgray-200 rounded-3xl overflow-hidden hover:border-emerald2 hover:shadow-md transition-all group">
                    <div className="aspect-video bg-warmgray-100 overflow-hidden">
                      {a.featured_image_url ? <img src={a.featured_image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" /> : <div className="w-full h-full flex items-center justify-center font-serif text-warmgray-500 text-4xl">{a.title?.[0]}</div>}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald2">{a.category} · {a.language?.toUpperCase()}</div>
                      <h3 className="font-serif text-xl text-charcoal mt-2 line-clamp-2">{a.title}</h3>
                      {a.summary && <p className="text-sm text-warmgray-700 mt-2 line-clamp-2">{a.summary}</p>}
                      <div className="mt-3 text-[10px] text-warmgray-500">{a.author || "—"}{a.publish_date && ` · ${a.publish_date}`}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

export function LearningArticlePage() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/articles/${slug}`);
        setArticle(r.data.article);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.seo_title || article.title} · Nail Viet Club`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", article.seo_description || article.summary || "");
    }
  }, [article]);

  // Naive Markdown-ish renderer: paragraphs + line breaks. Body may also be HTML.
  const rendered = useMemo(() => {
    if (!article) return "";
    const body = article.body || "";
    // If body contains HTML tags, render as-is. Else, paragraphize.
    if (/<\/?[a-z][\s\S]*>/i.test(body)) return { __html: body };
    return { __html: body.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br />")}</p>`).join("") };
  }, [article]);

  return (
    <div className="min-h-screen bg-white text-charcoal">
      <SiteHeader />
      <main>
        {loading ? (
          <div className="py-32 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
        ) : notFound || !article ? (
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center">
            <h1 className="font-serif text-4xl text-charcoal">{L("Không tìm thấy bài viết", "Article not found")}</h1>
            <Link to="/learn" className="inline-flex items-center gap-1 text-emerald2 hover:text-emerald2-600 mt-4"><ArrowLeft className="w-4 h-4" /> {L("Quay lại Trung Tâm", "Back to Learning Center")}</Link>
          </div>
        ) : (
          <article>
            {article.featured_image_url && (
              <div className="aspect-[21/9] bg-warmgray-100 overflow-hidden">
                <img src={article.featured_image_url} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
              <Link to="/learn" className="inline-flex items-center gap-1 text-emerald2 hover:text-emerald2-600 text-sm font-medium" data-testid="article-back">
                <ArrowLeft className="w-4 h-4" /> {L("Trung Tâm Học Tập", "Learning Center")}
              </Link>
              <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mt-6 text-emerald2">{article.category}</p>
              <h1 className="font-serif text-4xl md:text-5xl mt-3 text-charcoal leading-tight" data-testid="article-title">{article.title}</h1>
              {article.summary && <p className="mt-5 text-lg text-warmgray-700">{article.summary}</p>}
              <div className="mt-6 flex items-center gap-3 text-xs text-warmgray-500 border-b border-warmgray-200 pb-6">
                {article.author && <span>{article.author}</span>}
                {article.publish_date && <><span>·</span><span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {article.publish_date}</span></>}
                <span>·</span><span>{article.language?.toUpperCase()}</span>
              </div>
              <div className="prose prose-lg max-w-none mt-8 text-charcoal" data-testid="article-body" dangerouslySetInnerHTML={rendered} />
              {article.download_urls?.length > 0 && (
                <div className="mt-10 border-t border-warmgray-200 pt-8">
                  <h3 className="font-serif text-xl text-charcoal">{L("Tài liệu liên quan", "Related downloads")}</h3>
                  <div className="mt-3 space-y-2">
                    {article.download_urls.map((d, i) => (
                      <a key={i} href={d.url} target="_blank" rel="noopener noreferrer" data-testid={`article-download-${i}`}
                        className="flex items-center gap-2 text-sm text-emerald2 hover:text-emerald2-600">
                        <Download className="w-4 h-4" /> {d.title || d.url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
